import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Edit3, Brain,
  AlertCircle, Clock, User, Search, Send, Calendar, CheckCircle, XCircle, Trash2,
  UserX
} from 'lucide-react';
import { uploadsApi, candidatesApi } from '../../services/api';
import { analysisApi } from '../../services/api';
import websocketManager from '../../services/websocketManager';
import { useNeedAnalysisPolling } from '../../hooks/useAnalysisPolling';
import EditCandidateModal from './EditCandidateModal';
import CandidateDetailModal from './CandidateDetailModal';
import { useSofteamboardStore, normalizeStep } from '../../store/softeamboardStore';
import { useAuth } from '../../contexts/AuthContext';
import { throttle } from 'lodash';
import { ConfirmDialog } from '../shared/ConfirmDialog';
import Toast from '../ui/Toast';

const STEP_DISPLAY_NAMES = {
  'NO_CANDIDATE': 'Sans étape',
  'CANDIDATE_IDENTIFIED': 'Identifié',
  'CANDIDATE_SENT': 'Envoyé au client',
  'CANDIDATE_INTERVIEW': 'Entretien',
  'GO_NOGO': 'Décision GO / NO-GO',
  'SALE': 'Gagné',
  'CANCELED': 'Annulé',
  'DELETED': 'Clôturé'
};

const NEXT_STEP_ACTIONS = {
  'NO_CANDIDATE': 'Qualifier le profil',
  'CANDIDATE_IDENTIFIED': 'Vérifier puis envoyer au client',
  'CANDIDATE_SENT': "Relancer ou planifier l'entretien",
  'CANDIDATE_INTERVIEW': 'Saisir la décision du client',
  'GO_NOGO': 'Finaliser la décision',
  'SALE': 'Processus terminé',
  'CANCELED': 'Aucune action attendue',
  'DELETED': 'Aucune action attendue'
};

const getStepDisplayName = (stepType) => STEP_DISPLAY_NAMES[stepType] || 'Unknown';

const CandidateCard = ({ candidate, needId, onUpdate, onAnalyzeCV, onRateCandidate, onShowTooltip, onHideTooltip }) => {
  // Remove the debug log that's causing spam
  // console.log('🔍 CandidateCard: Component rendered with props:', { ... });

  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [isScoring, setIsScoring] = useState(false);
  const [scoreJobId, setScoreJobId] = useState(null);
  const [candidateData, setCandidateData] = useState(candidate);
  const [relevanceScore, setRelevanceScore] = useState(() => {
    // Check multiple possible score fields and prioritize non-null values
    const score = candidate.relevanceScore ?? candidate.relevance_score ?? null;
    // ✅ PERFORMANCE: Removed console.log from hot path (runs on every card mount)
    return score;
  });
  const [scoreJustification, setScoreJustification] = useState(() => {
    const justification = candidate.scoreJustification ?? candidate.score_justification ?? null;
    // ✅ PERFORMANCE: Removed console.log from hot path
    return justification;
  });
  const [currentStep, setCurrentStep] = useState(null);
  const [stepLoading, setStepLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [stepError, setStepError] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [showDetachConfirm, setShowDetachConfirm] = useState(false);
  const [toast, setToast] = useState(null);

  // ✅ NEW: Get store functions for updating candidate cache
  const { 
    refreshCandidatesForNeed,
    getStepsForCandidate,  // ✅ ADD: Get steps from store
    loadStepsForCandidate,  // ✅ ADD: Load steps to store
    getScoringDataForCandidateNeed  // ✅ ADD: Get scoring data for specific candidate-need
  } = useSofteamboardStore();
  const { user } = useAuth();
  const isRecruiter = user?.role === 'Recruiter';

  // ✅ FIXED: Use context-aware scoring lookup instead of candidate object properties
  const scoreData = useMemo(() => {
    // Get scoring data for this specific candidate-need combination
    const scoring = getScoringDataForCandidateNeed(candidate.id, needId);
    
    const score = scoring?.score ?? null;
    const justification = scoring?.justification ?? null;
    const isValidScore = score !== null && score !== undefined && !isNaN(score);
    const hasScore = isValidScore && !isScoring;
    
    // Only log when score actually changes to reduce console spam
    if (process.env.NODE_ENV === 'development' && score !== relevanceScore) {
      console.log('🔍 CandidateCard - Score data calculated:', { 
        candidateId: candidate.id,
        needId,
        score,
        hasScore,
        isScoring
      });
    }
    
    return {
      score,
      justification,
      isValidScore,
      hasScore,
      scoreJobId: scoring?.scoreJobId ?? null,
      scoredAt: scoring?.scoredAt ?? null
    };
  }, [
    candidate.id,
    needId,
    getScoringDataForCandidateNeed,
    relevanceScore, 
    isScoring
  ]);

  // Sync score from store only when store has a valid score (don't overwrite local WebSocket updates with null)
  useEffect(() => {
    if (scoreData.score !== null && scoreData.score !== relevanceScore) {
      setRelevanceScore(scoreData.score);
    }
    if (scoreData.justification !== null && scoreData.justification !== scoreJustification) {
      setScoreJustification(scoreData.justification);
    }
  }, [scoreData.score, scoreData.justification, relevanceScore, scoreJustification]);

  // ✅ NEW: Throttled store refresh to prevent excessive API calls
  const throttledStoreRefresh = useCallback(
    throttle(() => refreshCandidatesForNeed(needId), 2000, { trailing: true }),
    [needId, refreshCandidatesForNeed]
  );

  // ✅ PERFORMANCE FIX: Memoize candidate data to prevent excessive API calls
  const candidateDataMemo = useMemo(() => {
    return {
      ...candidate,
      relevanceScore: scoreData.score,
      scoreJustification: scoreData.justification
    };
  }, [candidate, scoreData.score, scoreData.justification]);

  // ✅ PERFORMANCE FIX: Only fetch candidate data if ID changes, not on every render
  useEffect(() => {
    const fetchCandidateData = async () => {
      if (!candidate?.id) return;

      // ✅ FIX: Check if we need to fetch data
      const needsFetch = !candidate.cvAnalysis &&
                         !candidate.cv_analysis &&
                         !candidate.cvHardSkillsJson &&
                         !candidate.cv_hard_skills_json;

      if (!needsFetch) {
        // Already have complete data, use it immediately
        setCandidateData(candidateDataMemo);
        setIsLoadingDetails(false);
        return;
      }

      // ✅ FIX: Set loading state before fetching
      setIsLoadingDetails(true);

      try {
        const fullCandidate = await candidatesApi.getCandidate(candidate.id);
        setCandidateData(fullCandidate);
        setIsLoadingDetails(false);
      } catch (error) {
        console.error('Failed to fetch candidate data:', error);
        setCandidateData(candidate);
        setIsLoadingDetails(false);
      }
    };

    fetchCandidateData();
  }, [candidate?.id, candidateDataMemo]);

  // ✅ PERFORMANCE FIX: Memoize skills calculations
  const skillsData = useMemo(() => {
  const getSkills = () => {
    if (candidateData.cvAnalysis?.hardskills) {
      return candidateData.cvAnalysis.hardskills;
    }
    if (candidateData.cvHardSkillsJson) {
      try {
        return JSON.parse(candidateData.cvHardSkillsJson);
      } catch (e) {
        console.error('Error parsing cvHardSkillsJson:', e);
      }
    }
    return candidateData.skills || [];
  };

  const getKeyStrengths = () => {
    if (candidateData.cvAnalysis?.keystrengths) {
      return candidateData.cvAnalysis.keystrengths;
    }
    if (candidateData.cvKeyStrengthsJson) {
      try {
        return JSON.parse(candidateData.cvKeyStrengthsJson);
      } catch (e) {
        console.error('Error parsing cvKeyStrengthsJson:', e);
      }
    }
    return [];
  };

  const getSoftSkills = () => {
    if (candidateData.cvAnalysis?.softskills) {
      return candidateData.cvAnalysis.softskills;
    }
    if (candidateData.cvSoftSkillsJson) {
      try {
        return JSON.parse(candidateData.cvSoftSkillsJson);
      } catch (e) {
        console.error('Error parsing cvSoftSkillsJson:', e);
      }
    }
    return [];
  };

    return {
      skills: getSkills(),
      keyStrengths: getKeyStrengths(),
      softSkills: getSoftSkills()
    };
  }, [candidateData.cvAnalysis, candidateData.cvHardSkillsJson, candidateData.cvKeyStrengthsJson, candidateData.cvSoftSkillsJson, candidateData.skills]);

  // stepHelpers extracted as module-level constants (STEP_PROGRESS, STEP_DISPLAY_NAMES)

  // ✅ FIX: Add request deduplication for API calls
  const pendingRequests = useRef(new Set());


  // ✅ FIX: Add helper function to get the most advanced step in workflow
  const getMostAdvancedStep = useCallback((steps) => {
    if (!steps || steps.length === 0) {
      return { type: 'NO_CANDIDATE', date: new Date().toISOString() };
    }


    // ✅ FIX: Define workflow progression order (least advanced first, most advanced last)
    const workflowProgression = [
      'NO_CANDIDATE',
      'CANDIDATE_IDENTIFIED',
      'CANDIDATE_SENT',
      'CANDIDATE_INTERVIEW',
      'GO_NOGO',
      'SALE',
      'CANCELED',
      'DELETED'
    ];

    // ✅ FIX: Find the most advanced step in the workflow
    let mostAdvancedStep = null;
    let highestIndex = -1;

    steps.forEach(step => {
      // ✅ FIXED: Don't skip NO_GO decisions - they should be considered as advanced steps
      // The display logic will handle showing them as GO_NOGO_NO_GO with red NO tag
      const stepIndex = workflowProgression.indexOf(step.type);
      if (stepIndex > highestIndex) {
        highestIndex = stepIndex;
        mostAdvancedStep = step;
      }
    });

    const result = mostAdvancedStep || { type: 'NO_CANDIDATE', date: new Date().toISOString() };


    return result;
  }, [candidate?.id, needId]);

  // ✅ FIX: Replace the old getLatestStep function with getMostAdvancedStep
  useEffect(() => {
    if (!needId || !candidate?.id) return;

    const requestKey = `${needId}_${candidate.id}`;
    
    // ✅ FIX: Don't make duplicate requests
    if (pendingRequests.current.has(requestKey)) {
      console.log('🔍 CandidateCard - Request already pending, skipping');
      return;
    }

    // ✅ FIX: Use store functions instead of direct API calls
    const cachedSteps = getStepsForCandidate(needId, candidate.id);
    
    if (cachedSteps.length > 0) {
      // Use cached data with proper workflow progression logic
      const mostAdvancedStep = getMostAdvancedStep(cachedSteps);
      setCurrentStep(mostAdvancedStep);
      setStepError(null);
    } else if (!stepLoading && !stepError) {
      // Load from store (which will fetch from API if needed)
      pendingRequests.current.add(requestKey);
      setStepLoading(true);
      
      loadStepsForCandidate(needId, candidate.id)
        .then(steps => {
          if (!steps) return;
          const normalizedSteps = steps.map(normalizeStep);
          const mostAdvancedStep = getMostAdvancedStep(normalizedSteps);
          setCurrentStep(mostAdvancedStep);
          setStepError(null);
        })
        .catch(error => {
          console.error('🔍 CandidateCard: Error loading steps:', error);
          setStepError(error);
          setCurrentStep(null);
        })
        .finally(() => {
          setStepLoading(false);
          pendingRequests.current.delete(requestKey);
        });
    }
  }, [needId, candidate?.id, stepError, getStepsForCandidate, loadStepsForCandidate, getMostAdvancedStep]);

  // Polling for CV analysis completion
  const { isPolling } = useNeedAnalysisPolling(
    needId,
    candidateData?.cvAnalysisStatus === 'PENDING',
    async (nId) => {
      return candidateData;
    },
    (updatedData) => {
      if (onUpdate && updatedData) {
        onUpdate(updatedData);
      }
    }
  );

  // ✅ FIX: Add message deduplication for CandidateCard too
  const processedScoreMessages = useRef(new Set());

  // ENHANCED: WebSocket for relevance scoring updates AND step updates
  const handleWebSocketMessage = useCallback((message) => {
    try {
      // ✅ FIX: Deduplicate scoring messages
      if (message.event_type === 'scoring_analysis_complete') {
        const messageId = `${message.event_type}_${message.entity_id}_${candidate.id}_${needId}`;
        
        if (processedScoreMessages.current.has(messageId)) {
          console.log('🔌 CandidateCard - Skipping duplicate scoring message');
          return;
        }
        processedScoreMessages.current.add(messageId);
        
        // Clear old messages (keep only last 10)
        if (processedScoreMessages.current.size > 10) {
          const firstMessage = processedScoreMessages.current.values().next().value;
          processedScoreMessages.current.delete(firstMessage);
        }
      }
      
      switch (message.event_type || message.type) {
        case 'scoring_analysis_complete': {
          console.log('🔌 CandidateCard - Scoring analysis complete:', message);
          
          const scoringData = message.payload || message.data || {};
          const messageCandidateId = scoringData.candidate_id;
          const messageNeedId = scoringData.need_id;
          const messageScore = scoringData.score;
          let messageJustification = scoringData.justification;
          
          // ✅ FIX: Ensure justification is always a string
          if (messageJustification && typeof messageJustification === 'object') {
            messageJustification = JSON.stringify(messageJustification, null, 2);
          }
          
          console.log('🔌 CandidateCard - Scoring data:', { 
            messageCandidateId, 
            messageNeedId, 
            messageScore, 
            messageJustification,
            currentCandidateId: candidate.id,
            currentNeedId: needId,
            scoreJobId
          });
          
          const isForThisCandidate = (
            (messageCandidateId === candidate.id && messageNeedId === needId) ||
            (message.entity_id === scoreJobId && scoreJobId !== null) ||
            (message.entity_id === candidate.id && scoringData.need_id === needId)
          );
          
          if (isForThisCandidate && messageScore !== undefined) {
            console.log('🔌 CandidateCard - ✅ Updating score for this candidate:', messageScore);
            
            setRelevanceScore(messageScore);
            setScoreJustification(messageJustification); // Now guaranteed to be a string
            setIsScoring(false);
            
            if (onUpdate) {
              onUpdate({
                ...candidateData,
                relevance_score: messageScore,
                score_justification: messageJustification, // String format
                relevanceScore: messageScore,
                scoreJustification: messageJustification // String format
              });
            }
          } else {
            console.log('🔌 CandidateCard - ❌ Scoring message not for this candidate');
          }
          break;
        }
          
        case 'candidate_analysis_complete':
          if (message.entity_id === candidate.id) {
            if (onUpdate) {
              // FIXED: Don't spread message.data directly, only set specific fields
              const analysisData = message.data;
              
              // Only extract safe, known fields from the analysis data
              const safeAnalysisFields = {};
              if (analysisData && typeof analysisData === 'object') {
                // Extract only the fields we know how to handle
                safeAnalysisFields.cvRole = analysisData.role;
                safeAnalysisFields.cv_role = analysisData.role;
                safeAnalysisFields.cvExperienceYears = analysisData.experienceyears;
                safeAnalysisFields.cv_experience_years = analysisData.experienceyears;
                safeAnalysisFields.cvTrigramme = analysisData.trigramme;
                safeAnalysisFields.cv_trigramme = analysisData.trigramme;
                safeAnalysisFields.cvCity = analysisData.city;
                safeAnalysisFields.cv_city = analysisData.city;
                safeAnalysisFields.cvCountry = analysisData.country;
                safeAnalysisFields.cv_country = analysisData.country;
                safeAnalysisFields.cvAvailability = analysisData.availability;
                safeAnalysisFields.cv_availability = analysisData.availability;
                
                // Store skills as JSON strings (safe)
                safeAnalysisFields.cvHardSkillsJson = JSON.stringify(analysisData.hardskills || []);
                safeAnalysisFields.cv_hard_skills_json = JSON.stringify(analysisData.hardskills || []);
                safeAnalysisFields.cvSoftSkillsJson = JSON.stringify(analysisData.softskills || []);
                safeAnalysisFields.cv_soft_skills_json = JSON.stringify(analysisData.softskills || []);
                safeAnalysisFields.cvKeyStrengthsJson = JSON.stringify(analysisData.keystrengths || []);
                safeAnalysisFields.cv_key_strengths_json = JSON.stringify(analysisData.keystrengths || []);
                
                // Experience data (safe)
                if (analysisData.experience1) {
                  safeAnalysisFields.cv1stExperienceTitle = analysisData.experience1.title;
                  safeAnalysisFields.cv_1st_experience_title = analysisData.experience1.title;
                  safeAnalysisFields.cv1stExperienceCompany = analysisData.experience1.company;
                  safeAnalysisFields.cv_1st_experience_company = analysisData.experience1.company;
                  safeAnalysisFields.cv1stExperienceStartDate = analysisData.experience1.startdate;
                  safeAnalysisFields.cv_1st_experience_start_date = analysisData.experience1.startdate;
                  safeAnalysisFields.cv1stExperienceEndDate = analysisData.experience1.enddate;
                  safeAnalysisFields.cv_1st_experience_end_date = analysisData.experience1.enddate;
                  safeAnalysisFields.cv1stExperienceLocation = analysisData.experience1.location;
                  safeAnalysisFields.cv_1st_experience_location = analysisData.experience1.location;
                }
                
                // Store the analysis as JSON string (safe)
                safeAnalysisFields.cvAnalysis = analysisData;
                safeAnalysisFields.cv_analysis_json = JSON.stringify(analysisData);
              }
              
              onUpdate({
                ...candidateData,
                cvAnalysisStatus: 'READY',
                cv_analysis_status: 'READY',
                ...safeAnalysisFields
              });
            }
          }
          break;
          
        case 'candidate_updated':
          if (message.entity_id === candidate.id) {
            // Handle candidate detachment
            if (message.payload?.action === 'DETACHED' && message.payload?.need_id === needId) {
              console.log('🔌 CandidateCard - This candidate was detached from current need');
              // Notify parent component to remove this candidate
              if (onUpdate) {
                onUpdate({ action: 'DETACHED', candidateId: candidate.id });
              }
              return;
            }
            
            if (onUpdate) {
              // FIXED: Be more careful about what we spread
              const updateData = message.payload;
              const safeUpdate = {};
              
              // Only copy known safe fields (strings, numbers, booleans, arrays)
              const safeFields = [
                'name', 'email', 'phone', 'skills', 'status', 'priority',
                'cvUrl', 'cvAnalysisStatus', 'cv_analysis_status',
                'cvRole', 'cv_role', 'cvExperienceYears', 'cv_experience_years',
                'cvTrigramme', 'cv_trigramme', 'cvCity', 'cv_city',
                'cvCountry', 'cv_country', 'cvAvailability', 'cv_availability',
                'relevanceScore', 'relevance_score', 'scoreJustification', 'score_justification'
              ];
              
              safeFields.forEach(field => {
                if (updateData && updateData.hasOwnProperty(field)) {
                  const value = updateData[field];
                  // Only include primitive values or arrays, not objects
                  if (typeof value !== 'object' || Array.isArray(value) || value === null) {
                    safeUpdate[field] = value;
                  }
                }
              });
              
              onUpdate({ ...candidateData, ...safeUpdate });
            }
          }
          break;
          
        // NEW: Handle step updates via WebSocket
        case 'step_deleted':
          // If this step was deleted and affects this candidate, clear step cache
          if (message.payload?.candidate_id === candidate.id || message.payload?.candidateId === candidate.id) {
            console.log('🔌 CandidateCard - Step deleted for this candidate, refreshing from store');
            // ✅ FIX: Use store function instead of direct API call
            loadStepsForCandidate(needId, candidate.id)
              .then(steps => {
                const normalizedSteps = steps.map(normalizeStep);
                const mostAdvancedStep = getMostAdvancedStep(normalizedSteps);
                setCurrentStep(mostAdvancedStep);
              })
              .catch(console.error);
          }
          break;
          
        case 'step_created':
        case 'step_updated':
          // If this step affects this candidate, refresh step data
          if (message.payload?.candidate_id === candidate.id || message.payload?.candidateId === candidate.id) {
            console.log('🔌 CandidateCard - Step created/updated for this candidate, refreshing from store');
            // ✅ FIX: Use store function instead of direct API call
            loadStepsForCandidate(needId, candidate.id)
              .then(steps => {
                const normalizedSteps = steps.map(normalizeStep);
                const mostAdvancedStep = getMostAdvancedStep(normalizedSteps);
                setCurrentStep(mostAdvancedStep);
              })
              .catch(console.error);
          }
          break;
          
        default:
          break;
      }
    } catch (error) {
      console.error('🔌 CandidateCard - Error handling WebSocket message:', error);
      if (isScoring) {
        setIsScoring(false);
      }
    }
  }, [scoreJobId, candidateData, needId, onUpdate, candidate.id, isScoring, throttledStoreRefresh, loadStepsForCandidate, getMostAdvancedStep]);

  // Job-scoped WebSocket subscription only when a scoring job is active
  useEffect(() => {
    if (!scoreJobId) return;
    const unsubscribe = websocketManager.subscribeToJob(scoreJobId, handleWebSocketMessage);
    return unsubscribe;
  }, [scoreJobId, handleWebSocketMessage]);

  // Handle CV download
  const handleDownloadCV = async (e) => {
    e.stopPropagation();
    try {
      // Use the configured API service instead of direct fetch
      const response = await uploadsApi.downloadCv(candidateData.id);
      const downloadUrl = response.downloadUrl;
      const filename = response.originalFilename || `${candidateData.name || 'candidate'}_CV.pdf`;

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading CV:', error);
      setToast({ message: 'Failed to download CV. Please try again.', type: 'error' });
    }
  };

  // Handle AI Relevance Scoring
  const handleAIRating = async (e) => {
    e.stopPropagation();
    
    if (!needId || !candidateData.id) {
      console.error('Missing needId or candidate.id for relevance scoring');
      return;
    }

    try {
      setIsScoring(true);
      
      // ✅ FIX: Use the new analysisApi.scoreRelevance function
      const result = await analysisApi.scoreRelevance(needId, candidateData.id);
      setScoreJobId(result.score_job_id);
      
    } catch (error) {
      console.error('Error starting relevance scoring:', error);
      setIsScoring(false);
      setToast({ message: 'Failed to start relevance scoring. Please try again.', type: 'error' });
    }
  };

  // Handle Edit
  const handleEdit = (e) => {
    e.stopPropagation();
    setShowEditModal(true);
  };

  // Handle card click to show detail modal
  const handleCardClick = () => {
    setShowDetailModal(true);
  };

  // Handle edit success
  const handleEditSuccess = (updatedCandidate) => {
    setCandidateData(updatedCandidate);
    if (onUpdate) {
      onUpdate(updatedCandidate);
    }
  };

  // Get CV analysis status badge
  const getCVAnalysisStatusBadge = () => {
    const status = candidateData.cvAnalysisStatus || candidateData.cv_analysis_status;
    if (!status) return null;
    
    const statusConfig = {
      PENDING: { color: 'bg-yellow-100 text-yellow-800', text: 'CV Analysis Pending', icon: Clock },
      READY: { color: 'bg-green-100 text-green-800', text: 'CV Analysis Ready', icon: Brain },
      ERROR: { color: 'bg-red-100 text-red-800', text: 'CV Analysis Error', icon: AlertCircle }
    };
    
    const config = statusConfig[status];
    if (!config) return null;
    
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon size={12} className="mr-1" />
        {config.text}
        {isPolling && status === 'PENDING' && (
          <span className="ml-1 animate-pulse">...</span>
        )}
      </span>
    );
  };

  // Get step progress bar color
  const getStepStatusColor = (stepType) => {
    const colorMap = {
      'NO_CANDIDATE': 'bg-gray-100 text-gray-800',
      'CANDIDATE_IDENTIFIED': 'bg-blue-100 text-blue-800',
      'CANDIDATE_SENT': 'bg-yellow-100 text-yellow-800',
      'CANDIDATE_INTERVIEW': 'bg-purple-100 text-purple-800',
      'GO_NOGO': 'bg-orange-100 text-orange-800',
      'SALE': 'bg-green-100 text-green-800',
      'CANCELED': 'bg-red-100 text-red-800',
      'DELETED': 'bg-gray-100 text-gray-800'
    };
    return colorMap[stepType] || 'bg-gray-100 text-gray-800';
  };

  const getStepStatusIcon = (stepType) => {
    const iconMap = {
      'NO_CANDIDATE': <User className="w-3 h-3 mr-1" />,
      'CANDIDATE_IDENTIFIED': <Search className="w-3 h-3 mr-1" />,
      'CANDIDATE_SENT': <Send className="w-3 h-3 mr-1" />,
      'CANDIDATE_INTERVIEW': <Calendar className="w-3 h-3 mr-1" />,
      'GO_NOGO': <AlertCircle className="w-3 h-3 mr-1" />,
      'SALE': <CheckCircle className="w-3 h-3 mr-1" />,
      'CANCELED': <XCircle className="w-3 h-3 mr-1" />,
      'DELETED': <Trash2 className="w-3 h-3 mr-1" />
    };
    return iconMap[stepType] || <User className="w-3 h-3 mr-1" />;
  };

  const skills = skillsData.skills;

  // ✅ FIX: Add utility function to safely convert justification to string
  const formatScoreJustification = (justification) => {
    if (!justification) return null;
    
    // If it's already a string, return as-is
    if (typeof justification === 'string') {
      return justification;
    }
    
    // If it's an object, format it nicely
    if (typeof justification === 'object') {
      try {
        // Pretty format the JSON object
        return JSON.stringify(justification, null, 2);
      } catch (error) {
        console.error('Error formatting score justification:', error);
        return 'Score justification available (formatting error)';
      }
    }
    
    // Fallback for other types
    return String(justification);
  };

  // ✅ FIX: Update the tooltip handler to safely format the justification
  const handleTooltipShow = (e) => {
    if (scoreJustification && onShowTooltip) {
      const formattedJustification = formatScoreJustification(scoreJustification);
      onShowTooltip(formattedJustification, e);
    }
  };

  const handleTooltipHide = () => {
    if (onHideTooltip) {
      onHideTooltip();
    }
  };

  // Open detach confirmation
  const handleDetachFromNeed = (e) => {
    e.stopPropagation();

    if (!needId || !candidateData.id) {
      console.error('Missing needId or candidate.id for detaching');
      return;
    }

    setShowDetachConfirm(true);
  };

  // Perform actual detach
  const confirmDetach = async () => {
    try {
      setIsDeleting(true);

      if (onUpdate) {
        onUpdate({ action: 'DETACHED', candidateId: candidateData.id });
      }

      await candidatesApi.removeCandidate(needId, candidateData.id);
      setToast({ message: 'Candidate removed from need', type: 'success' });
    } catch (error) {
      console.error('Error detaching candidate from need:', error);

      if (onUpdate) {
        onUpdate({ action: 'REATTACH', candidateId: candidateData.id, candidate: candidateData });
      }

      let errorMessage = 'Failed to remove candidate from need.';
      if (error.response?.status === 403 || error.message.includes('Admin privileges') || error.message.includes('403')) {
        errorMessage = 'You need admin privileges to remove candidates from needs. Please contact an administrator.';
      } else if (error.message.includes('not found') || error.response?.status === 404) {
        errorMessage = 'Candidate is not associated with this need.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      setToast({ message: errorMessage, type: 'error' });
    } finally {
      setIsDeleting(false);
      setShowDetachConfirm(false);
    }
  };

  // ✅ NEW: Show loading skeleton while fetching candidate details
  if (isLoadingDetails) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 animate-pulse">
        <div className="flex items-start space-x-3 mb-3">
          <div className="w-12 h-12 bg-gray-300 rounded-full flex-shrink-0"></div>
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-gray-300 rounded w-3/4"></div>
            <div className="h-3 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
        <div className="space-y-2">
          <div className="h-3 bg-gray-200 rounded"></div>
          <div className="h-3 bg-gray-200 rounded w-5/6"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className="group relative flex min-h-[280px] cursor-pointer flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition-shadow hover:shadow-md focus-within:ring-2 focus-within:ring-blue-500"
        onClick={handleCardClick}
      >
        <div className="border-b border-gray-100 p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 flex-1 items-start gap-3">
              <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-blue-600 text-sm font-bold text-white">
                {candidateData.name?.charAt(0)?.toUpperCase() || '?'}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="truncate text-base font-semibold text-gray-900">
                    {candidateData.name || 'Unknown Candidate'}
                    </h3>
                    {(candidateData.cvRole || candidateData.cv_role) && (
                      <p className="mt-0.5 truncate text-sm text-gray-600">
                        {candidateData.cvRole || candidateData.cv_role}
                      </p>
                    )}
                  </div>
                  {scoreData.hasScore ? (
                    <div
                      className="flex flex-shrink-0 items-center rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700"
                      onMouseEnter={handleTooltipShow}
                      onMouseLeave={handleTooltipHide}
                    >
                      <Brain size={12} className="mr-1" />
                      {scoreData.score}%
                    </div>
                  ) : !isRecruiter ? (
                    <button
                      onClick={handleAIRating}
                      disabled={isScoring || !needId}
                      className="flex min-h-[36px] flex-shrink-0 items-center rounded-md border border-blue-200 bg-blue-50 px-2.5 text-xs font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-50"
                    >
                      <Brain size={12} className="mr-1" />
                      {isScoring ? 'Analyse...' : 'Noter IA'}
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2 border-t border-gray-100 pt-3 text-xs">
            <div className="text-gray-500">
              Disponibilité
              <strong className="mt-0.5 block truncate font-medium text-gray-800">
                {candidateData.cvAvailability || candidateData.cv_availability || 'Non spécifiée'}
              </strong>
            </div>
            <div className="text-gray-500">
              Localisation
              <strong className="mt-0.5 block truncate font-medium text-gray-800">
                {[candidateData.cvCity || candidateData.cv_city, candidateData.cvCountry || candidateData.cv_country]
                  .filter(Boolean).join(', ') || 'Non spécifiée'}
              </strong>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5 px-4 py-3">
          {skills.slice(0, 4).map((skill, index) => (
            <span key={index} className="rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700">
              {skill}
            </span>
          ))}
          {skills.length > 4 && (
            <span className="rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-500">+{skills.length - 4}</span>
          )}
          {skills.length === 0 && <span className="text-xs text-gray-500">Compétences non renseignées</span>}
        </div>

        <div className="mt-auto border-t border-gray-100 p-4">
          <div className="flex items-center justify-between gap-2">
            <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${getStepStatusColor(currentStep?.type || 'NO_CANDIDATE')}`}>
              {getStepStatusIcon(currentStep?.type || 'NO_CANDIDATE')}
              {stepLoading ? 'Chargement...' : getStepDisplayName(currentStep?.type || 'NO_CANDIDATE')}
            </span>
            {getCVAnalysisStatusBadge()}
          </div>
          <p className="mt-3 text-xs text-gray-600">
            <strong className="font-medium text-blue-700">Prochaine action :</strong>{' '}
            {NEXT_STEP_ACTIONS[currentStep?.type || 'NO_CANDIDATE']}
          </p>
          <div className="mt-3 flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                handleCardClick();
              }}
              className="min-h-[44px] rounded-md bg-blue-600 px-4 text-sm font-medium text-white hover:bg-blue-700"
            >
              Voir le profil
            </button>
            {!isRecruiter && (
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={handleEdit}
                  className="flex min-h-[44px] items-center rounded-md px-3 text-sm text-gray-600 hover:bg-gray-100"
                >
                  <Edit3 size={14} className="mr-1" />
                  Modifier
                </button>
                <button
                  type="button"
                  onClick={handleDetachFromNeed}
                  disabled={isDeleting || !needId}
                  className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                  aria-label={`Retirer ${candidateData.name || 'le candidat'} du besoin`}
                >
                  {isDeleting ? <div className="h-4 w-4 animate-spin rounded-full border-2 border-red-600 border-t-transparent" /> : <UserX size={16} />}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      <EditCandidateModal
        candidate={candidateData}
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSuccess={handleEditSuccess}
      />

      <CandidateDetailModal
        candidate={candidateData}
        needId={needId}
        open={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        onEdit={() => {
          setShowDetailModal(false);
          setShowEditModal(true);
        }}
        onDownloadCV={handleDownloadCV}
      />

      <ConfirmDialog
        open={showDetachConfirm}
        title="Remove Candidate"
        message={`Remove ${candidateData.name || 'this candidate'} from this need? This will only remove the candidate from this specific need.`}
        confirmLabel="Remove"
        variant="danger"
        loading={isDeleting}
        onConfirm={confirmDetach}
        onCancel={() => setShowDetachConfirm(false)}
      />

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

// Memoize with default shallow comparison — custom comparator was blocking score updates
export default React.memo(CandidateCard);
